/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { Text, Title } from '@components/v2/ui';
import { TMesurabilityProject } from '@features/offsets/types';
import {
	Flex,
	NumberFormatter,
	Stack,
} from '@mantine/core';
import { getCurrencyUnit } from '@utils/currency-units';
import { getUnitLabel } from '@utils/units';
import { useTranslation } from 'react-i18next';
import { useOffsetSummary } from './hooks/useOffsetSummary';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const FinalOffsetSummary = ({
	quantityOffset,
	offsetMesurabilityProjectsSelected,
}: {
	quantityOffset: number;
	offsetMesurabilityProjectsSelected: TMesurabilityProject[];
}) => {
	/* ********************************** Hooks ********************************* */
	const { t } = useTranslation();

	const { totalExclVAT, averagePrice, totalInclVAT } =
		useOffsetSummary(offsetMesurabilityProjectsSelected);

	/* ******************************** RENDERING ******************************* */
	return (
		<Stack w="100%" mt="22px" mb="24px" gap="24px">
			<Stack gap="0">
				<Flex
					justify="space-between"
					align="center"
					mb="24px"
				>
					<Text
						variant="body2"
						c="var(--mantine-color-igluTextSecondary-9)"
					>
						{t('global.compensatedQuantity')}
					</Text>
					<Text
						variant="body2"
						c="var(--mantine-color-igluTextSecondary-9)"
					>
						{quantityOffset} {getUnitLabel('tco2e')}
					</Text>
				</Flex>

				<Flex justify="space-between" align="center">
					<Text
						variant="button"
						c="var(--mantine-color-igluTextPrimary-9)"
					>
						{t('global.averagePrice')}
					</Text>
					<Text
						variant="subtitle1"
						c="var(--mantine-color-igluBlue-9)"
					>
						<NumberFormatter
							thousandSeparator=" "
							decimalSeparator=","
							value={averagePrice}
							decimalScale={2}
							suffix={
								getCurrencyUnit('EUR') +
								'/' +
								getUnitLabel('tco2e')
							}
						/>
					</Text>
				</Flex>

				<Flex justify="space-between" align="center">
					<Text
						variant="button"
						c="var(--mantine-color-igluTextPrimary-9)"
					>
						{t('global.totalAmountExcludingVAT')}
					</Text>
					<Title
						order={6}
						c="var(--mantine-color-igluBlue-9)"
					>
						<NumberFormatter
							thousandSeparator=" "
							decimalSeparator=","
							value={totalExclVAT}
							decimalScale={2}
							suffix={getCurrencyUnit('EUR')}
						/>
					</Title>
				</Flex>

				<Flex justify="space-between" align="center">
					<Text
						variant="subtitle1"
						c="var(--mantine-color-igluTextPrimary-9)"
					>
						{t('global.totalAmountIncludingVAT')}
					</Text>
					<Title
						order={6}
						c="var(--mantine-color-igluBlue-9)"
					>
						<NumberFormatter
							thousandSeparator=" "
							decimalSeparator=","
							value={totalInclVAT}
							decimalScale={2}
							suffix={getCurrencyUnit('EUR')}
						/>
					</Title>
				</Flex>
			</Stack>
		</Stack>
	);
};
